import React, { useRef } from "react";
import { Flex, theme } from "antd";
import styled from "styled-components";
import { analyticsTheme } from "./analyticsTheme";
import { radius } from "@libertify/ui";

export type PillTab = {
  key: string;
  label: string;
  /** Color of the dot before the label; no dot when left out. */
  color?: string;
};

export type PillTabsProps = {
  tabs: PillTab[];
  activeKey: string;
  onChange: (key: string) => void;
};

/** Tabs drawn as pills, each with an optional colored dot; the selected pill is filled. */
export const PillTabs: React.FC<PillTabsProps> = ({ tabs, activeKey, onChange }) => {
  const { token } = theme.useToken();
  const pills = useRef<(HTMLButtonElement | null)[]>([]);

  // Arrow keys, Home and End move to another tab and select it; Tab leaves
  // the list (only the selected tab is in the tab order).
  const onKeyDown = (index: number) => (event: React.KeyboardEvent) => {
    const last = tabs.length - 1;
    const targets: Record<string, number> = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    const target = targets[event.key];
    const next = target == null ? undefined : tabs[target];
    if (target == null || !next) return;
    event.preventDefault();
    pills.current[target]?.focus();
    onChange(next.key);
  };

  return (
    <Flex wrap="wrap" gap={8} role="tablist">
      {tabs.map((pill, index) => {
        const selected = pill.key === activeKey;
        return (
          <StyledPill
            key={pill.key}
            ref={(el) => {
              pills.current[index] = el;
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            $selected={selected}
            $accent={token.colorPrimary}
            onClick={() => onChange(pill.key)}
            onKeyDown={onKeyDown(index)}
          >
            {pill.color ? <StyledDot aria-hidden style={{ background: pill.color }} /> : null}
            {pill.label}
          </StyledPill>
        );
      })}
    </Flex>
  );
};

// ─── Styled components ───

const StyledPill = styled.button<{ $selected: boolean; $accent: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: ${radius.pill}px;
  border: 1px solid ${({ $selected, $accent }) => ($selected ? $accent : analyticsTheme.cardBorder)};
  background: ${({ $selected, $accent }) => ($selected ? $accent : analyticsTheme.cardBg)};
  color: ${({ $selected }) => ($selected ? "#ffffff" : analyticsTheme.textBody)};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${({ $accent }) => $accent};
  }

  &:focus-visible {
    outline: 2px solid ${({ $accent }) => $accent};
    outline-offset: 2px;
  }
`;

const StyledDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: ${radius.circle};
  flex: none;
`;
